import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Input } from 'shared-components';
import { luhnCheck } from 'shared-components/src/validations/cardValidations';
import InputGroup from '../../../layouts/InputGroup';
import AuthLayout from '../layouts/Auth';
import { AuthForm } from '../layouts/InnerAuth';
import { FieldError, RecaptchaWrapper } from '../styles';
import { forgotUsername } from '../../../utils/auth';
import ReCAPTCHA from '../../../components/ReCaptcha';

// separators only: digitsOnly()'s /\D/g would launder letters into 16 digits
export const normalizeCardNumber = value =>
  String(value || '').replace(/[\s-]+/g, '');

// 16 mirrors bps-go SendInput.CardNumber validate:"len=16,number"
// luhnCheck does no stripping; feed it normalised digits
export const isWellFormedCardNumber = value => {
  const digits = normalizeCardNumber(value);
  return /^\d{16}$/.test(digits) && luhnCheck(digits);
};

const CARD_NUMBER_ERROR_ID = 'forgot-username-card-number-error';

const ForgotUsername = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const page = 'forgot-username';

  const [cardNumber, setCardNumber] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [formError, setFormError] = useState(null);
  const [fieldError, setFieldError] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef(null);

  const digits = normalizeCardNumber(cardNumber);
  const cardNumberValid = isWellFormedCardNumber(cardNumber);
  const canSubmit = cardNumberValid && Boolean(captchaToken);

  const handleCardNumberChange = e => {
    setCardNumber(e.target.value);
    setFieldError(null);
  };

  const handleCardNumberBlur = () => {
    setFieldError(
      cardNumber && !cardNumberValid ? 'errors-cardnumber-invalid' : null
    );
  };

  const handleCaptchaChange = token => {
    setCaptchaToken(token);
    setFormError(null);
  };

  // widget still mounted here; without reset submit can never re-enable
  const resetCaptcha = () => {
    setCaptchaToken(null);
    if (recaptchaRef.current) recaptchaRef.current.reset();
  };

  const handleCaptchaExpired = () => {
    resetCaptcha();
    setFormError('errors-recaptcha_timeout');
  };

  // onErrored, not onError — the library implements no onError
  const handleCaptchaError = () => {
    resetCaptcha();
    setFormError('errors-captcha_provider_unavailable');
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // disabled attribute alone leaves every other submit path unguarded
    if (!canSubmit) return;

    setFormError(null);
    setFieldError(null);
    // alerts render independently; success would stack under a later error
    setEmailSent(false);
    setLoading(true);

    try {
      // digits, not raw: bps-go strips only ' ' and '-'
      await forgotUsername({
        card_number: digits,
        locale: intl.locale,
        recaptcha_token: captchaToken,
      });
      setEmailSent(true);
      setCardNumber('');
    } catch (error) {
      const key = `errors-${error.message}`;
      setFormError(
        intl.messages[key] ? key : 'errors-forgot_username_failed'
      );
    } finally {
      setLoading(false);
      // no reset(): the loader already unmounted the widget that made this token
      setCaptchaToken(null);
    }
  };

  return (
    <AuthLayout
      page={page}
      heading={intl.messages[`${page}-heading`]}
      subHeaderOne={intl.messages[`${page}-subheader1-text`]}
      error={formError}
      success={emailSent ? `${page}-email-sent-text` : null}
    >
      <AuthForm
        page={page}
        onSubmit={handleSubmit}
        submitButtonDisabled={!canSubmit}
        loading={loading}
        secondaryButtonText={intl.messages[`${page}-secondary-button`]}
        secondaryButtonFunc={() => navigate('/login')}
      >
        <InputGroup
          label={intl.messages['auth-card-number-label']}
          labelFor="card-number"
          // page="auth": Label builds `${page}-${labelFor}-label`; only that key exists
          page="auth"
        >
          <Input
            id="card-number"
            className={fieldError ? 'is-invalid' : ''}
            value={cardNumber}
            onChange={handleCardNumberChange}
            onBlur={handleCardNumberBlur}
            inputMode="numeric"
            // UA truncates a paste before onChange; 19 eats a digit
            maxLength={24}
            autoComplete="off"
            aria-invalid={Boolean(fieldError)}
            aria-describedby={fieldError ? CARD_NUMBER_ERROR_ID : undefined}
          />
          {fieldError && (
            <FieldError id={CARD_NUMBER_ERROR_ID} className="invalid-feedback">
              {intl.messages[fieldError]}
            </FieldError>
          )}
        </InputGroup>
        <RecaptchaWrapper>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            onChange={handleCaptchaChange}
            onExpired={handleCaptchaExpired}
            onErrored={handleCaptchaError}
          />
        </RecaptchaWrapper>
      </AuthForm>
    </AuthLayout>
  );
};

export default ForgotUsername;
